// src/api.ts
import { createClient as createClient2 } from "@connectrpc/connect";
import {
  ActorService,
  AppealService,
  AuthService as AuthService2,
  CommunityService,
  DirectMessageService,
  E2eeService,
  FeedService,
  FilterListService,
  FilterService,
  LabelService,
  MediaService,
  ModerationService,
  NodeService,
  NotificationService,
  OnboardingService,
  PageService,
  PostService,
  PrivacyService,
  ReactionService,
  SocialGraphService,
  SystemService,
  TagService
} from "@patches/proto/es";

// src/session.ts
import { Code as Code2, ConnectError as ConnectError2, createClient } from "@connectrpc/connect";
import { AuthService } from "@patches/proto/es";

// src/errors.ts
import { Code, ConnectError } from "@connectrpc/connect";
function isSignInRequired(error) {
  return ConnectError.from(error).metadata.get("x-patches-error-code") === "SIGN_IN_REQUIRED";
}
function isPrivacyAckRequired(error) {
  return ConnectError.from(error).metadata.get("x-patches-error-code") === "PRIVACY_NOTICE_NOT_ACKNOWLEDGED";
}
function isDefinitiveAuthFailure(error) {
  const code = ConnectError.from(error).code;
  return code === Code.Unauthenticated || code === Code.PermissionDenied;
}
function isTransientError(error) {
  const connectError = ConnectError.from(error);
  if (connectError.code === Code.Unavailable || connectError.code === Code.DeadlineExceeded) {
    return true;
  }
  return connectError.code === Code.Unknown && connectError.cause instanceof TypeError;
}
function serverMessage(error) {
  const trimmed = error.rawMessage.trim();
  if (trimmed.length === 0 || trimmed.includes("\n    at ")) return void 0;
  return trimmed;
}
function combine(title, hint) {
  return hint === void 0 || hint.length === 0 ? title : `${title} ${hint}`;
}
function describeError(error, options) {
  const connectError = ConnectError.from(error);
  const code = connectError.code;
  const fromServer = serverMessage(connectError);
  const target = options?.target ?? "the server";
  const copy = options?.copy;
  function result(title, hint, retryable) {
    return { title, hint, message: combine(title, hint), retryable, code };
  }
  switch (code) {
    case Code.Unavailable:
      return result(
        `Can't reach the Patches server at ${target}.`,
        copy?.unavailableHint ?? "Check that it is running and reachable.",
        true
      );
    case Code.DeadlineExceeded:
      return result(
        `${target} took too long to answer.`,
        "The server may be overloaded. Try again in a moment.",
        true
      );
    case Code.Unauthenticated:
      if (isSignInRequired(connectError)) {
        return result(
          "This node requires sign-in to read.",
          copy?.signInRequiredHint ?? "Sign in or create an account to continue.",
          false
        );
      }
      if (options?.context === "credentials") {
        return result("Wrong handle/email or password.", "", false);
      }
      return result("Your session is no longer valid.", "Sign in again to continue.", false);
    case Code.PermissionDenied:
      return result(fromServer ?? "You do not have permission to do that.", "", false);
    case Code.FailedPrecondition:
      if (isPrivacyAckRequired(connectError)) {
        return result(
          copy?.privacyAckTitle ?? "This node's privacy notice changed \u2014 review and acknowledge it.",
          copy?.privacyAckHint ?? "Go to Settings \u2192 Privacy.",
          false
        );
      }
      return result(
        fromServer ?? "The server refused this request in its current state.",
        "",
        false
      );
    case Code.ResourceExhausted:
      return result(
        "You are going a bit fast for the server.",
        "Wait a few seconds and try again.",
        true
      );
    case Code.InvalidArgument:
      return result(fromServer ?? "The server rejected that request.", "", false);
    case Code.NotFound:
      return result(fromServer ?? "That no longer exists.", "", false);
    case Code.AlreadyExists:
      return result(fromServer ?? "That is already taken.", "", false);
    case Code.Unimplemented:
      return result(
        `${target} does not support this feature.`,
        "It may be running an older version of Patches.",
        false
      );
    case Code.Canceled:
      return result("Request cancelled.", "", true);
    case Code.Internal:
    case Code.Unknown:
      return result(
        "The server hit an unexpected problem.",
        fromServer ?? "Try again; if it keeps happening, report it.",
        true
      );
    default:
      return result(
        `Could not talk to ${target}.`,
        fromServer ?? "Check the address and your network connection.",
        true
      );
  }
}

// src/session.ts
var InMemoryCredentialStore = class {
  session;
  load() {
    return Promise.resolve(this.session);
  }
  save(session) {
    this.session = session;
    return Promise.resolve();
  }
  clear() {
    this.session = void 0;
    return Promise.resolve();
  }
};
var SESSION_REFRESHED_EVENT = "patches:session-refreshed";
var SIGNED_OUT_SNAPSHOT = Object.freeze({
  signedIn: false,
  expiresAt: void 0
});
function decodeJwtExpiry(token) {
  const segments = token.split(".");
  if (segments.length !== 3) return void 0;
  const payloadSegment = segments[1];
  if (payloadSegment === void 0) return void 0;
  try {
    const payload = JSON.parse(decodeBase64UrlUtf8(payloadSegment));
    if (typeof payload !== "object" || payload === null) return void 0;
    const exp = payload["exp"];
    if (typeof exp !== "number" || !Number.isFinite(exp)) return void 0;
    return exp * 1e3;
  } catch {
    return void 0;
  }
}
function decodeBase64UrlUtf8(value) {
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
function emitSessionRefreshed(accessToken) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  if (typeof CustomEvent !== "function") return;
  window.dispatchEvent(
    new CustomEvent(SESSION_REFRESHED_EVENT, {
      detail: { expiresAt: decodeJwtExpiry(accessToken) }
    })
  );
}
var SessionManager = class {
  authClient;
  store;
  storageKey;
  current;
  loaded = false;
  refreshing;
  listeners = /* @__PURE__ */ new Set();
  snapshot = SIGNED_OUT_SNAPSHOT;
  constructor(options) {
    this.authClient = createClient(AuthService, options.transport);
    this.store = options.credentialStore ?? new InMemoryCredentialStore();
    this.storageKey = options.storageKey;
    if (this.storageKey !== void 0 && typeof window !== "undefined" && typeof window.addEventListener === "function") {
      window.addEventListener("storage", this.handleStorageEvent);
    }
  }
  /**
   * Subscribes to every change of the held session — login, logout, refresh, or a
   * cross-tab `storage` event. Pairs with {@link getSnapshot} for `useSyncExternalStore`.
   * Subscribing also kicks the (async) credential-store load, so a fresh subscriber
   * isn't stuck on the pre-load signed-out snapshot when no RPC has run yet.
   */
  subscribe(listener) {
    this.listeners.add(listener);
    void this.ensureLoaded().catch(() => void 0);
    return () => {
      this.listeners.delete(listener);
    };
  }
  /** Cached snapshot — the same object reference until the session actually changes,
   * which is what `useSyncExternalStore`'s identity check requires. */
  getSnapshot() {
    return this.snapshot;
  }
  /**
   * Removes the `storage` listener registered in the constructor (B-174) — a no-op if
   * `storageKey` was never set or `window` never existed. Safe to call more than once.
   * Only needed by callers whose `SessionManager` doesn't live for the whole process:
   * a short-lived instance (e.g. one built per test, or per request in a multi-tenant
   * host) leaks one `window` listener per instance otherwise. An app-lifetime singleton
   * (e.g. `apps/web/src/api/client.ts`'s `sessionManager`) has no matching lifecycle
   * event to call this from and correctly never disposes — the listener dies with the
   * tab/process either way.
   */
  dispose() {
    if (this.storageKey !== void 0 && typeof window !== "undefined" && typeof window.removeEventListener === "function") {
      window.removeEventListener("storage", this.handleStorageEvent);
    }
  }
  /** The current access token's `exp` as ms since epoch, or `undefined` when signed
   * out / not yet loaded / the token has no parseable `exp` (B-169 proactive-refresh UI). */
  async getExpiresAt() {
    await this.ensureLoaded();
    return this.snapshot.expiresAt;
  }
  /** The current access token, if any — loads from the credential store on first use. */
  async getAccessToken() {
    await this.ensureLoaded();
    return this.current?.accessToken;
  }
  /** Replaces the held session (e.g. right after `Login`) and persists it. */
  async setSession(session) {
    this.current = session;
    this.loaded = true;
    await this.store.save(session);
    this.commit();
  }
  /** Drops the session (e.g. `Logout`). Does not call the server — callers issue the
   * `Logout` RPC themselves before or after clearing local state. */
  async clear() {
    this.current = void 0;
    this.loaded = true;
    await this.store.clear();
    this.commit();
  }
  /**
   * Runs `fn` with the current access token. If `fn` rejects with a Connect
   * `Code.Unauthenticated` error, refreshes once (single-flight — concurrent callers
   * share one in-flight refresh, ADR 0016 §9) and retries `fn` exactly once with the new
   * token. A second `Unauthenticated` is not retried again — it propagates, meaning the
   * refresh token itself is no longer valid and the caller must sign in again.
   */
  async withSession(fn) {
    const token = await this.getAccessToken();
    if (token === void 0) {
      throw new ConnectError2("Not signed in.", Code2.Unauthenticated);
    }
    try {
      return await fn(token);
    } catch (error) {
      if (!isUnauthenticated(error)) throw error;
      let refreshed;
      try {
        refreshed = await this.refresh();
      } catch (refreshError) {
        if (isDefinitiveAuthFailure(refreshError)) await this.clear();
        throw refreshError;
      }
      return fn(refreshed.accessToken);
    }
  }
  async ensureLoaded() {
    if (this.loaded) return;
    const stored = await this.store.load();
    if (this.loaded) return;
    this.current = stored;
    this.loaded = true;
    this.commit();
  }
  /**
   * `storage` listener (B-169 cross-tab): another tab wrote or removed our credential
   * key. Never parses `event.newValue` — the `CredentialStore` owns the encoding — just
   * re-reads through it so this tab adopts whatever the other tab persisted.
   */
  handleStorageEvent = (event) => {
    if (this.storageKey === void 0 || event.key !== this.storageKey) return;
    void this.reloadFromCredentialStore().catch(() => void 0);
  };
  async reloadFromCredentialStore() {
    this.current = await this.store.load();
    this.loaded = true;
    this.commit();
  }
  /** Recomputes the snapshot from the held session and wakes every subscriber. */
  commit() {
    const token = this.current?.accessToken;
    this.snapshot = token === void 0 ? SIGNED_OUT_SNAPSHOT : { signedIn: true, expiresAt: decodeJwtExpiry(token) };
    for (const listener of this.listeners) listener();
  }
  async refresh() {
    if (this.refreshing === void 0) {
      this.refreshing = this.doRefresh().finally(() => {
        this.refreshing = void 0;
      });
    }
    return this.refreshing;
  }
  async doRefresh() {
    await this.ensureLoaded();
    const refreshToken = this.current?.refreshToken;
    if (refreshToken === void 0) {
      throw new ConnectError2("No session to refresh.", Code2.Unauthenticated);
    }
    const stored = await this.store.load();
    if (stored !== void 0 && stored.refreshToken !== refreshToken) {
      this.current = stored;
      this.commit();
      emitSessionRefreshed(stored.accessToken);
      return stored;
    }
    const response = await this.authClient.refreshSession({ refreshToken });
    if (response.session === void 0) {
      throw new ConnectError2("Refresh returned no session.", Code2.Unknown);
    }
    const next = {
      accessToken: response.session.accessToken,
      refreshToken: response.session.refreshToken
    };
    await this.setSession(next);
    emitSessionRefreshed(next.accessToken);
    return next;
  }
};
function isUnauthenticated(error) {
  return error instanceof ConnectError2 && error.code === Code2.Unauthenticated;
}

// src/api.ts
var UNARY_DEADLINE_MS = 1e4;
var AUTH_DEADLINE_MS = 15e3;
var METADATA_KEYS = Object.freeze({
  requestId: "x-request-id",
  client: "x-patches-client",
  clientVersion: "x-patches-client-version"
});
function createPatchesApi(options) {
  const bind = (service, deadlineMs) => bindService(service, options.transport, deadlineMs, options.clientName, options.clientVersion);
  return {
    system: bind(SystemService, UNARY_DEADLINE_MS),
    auth: bind(AuthService2, AUTH_DEADLINE_MS),
    actors: bind(ActorService, UNARY_DEADLINE_MS),
    socialGraph: bind(SocialGraphService, UNARY_DEADLINE_MS),
    posts: bind(PostService, UNARY_DEADLINE_MS),
    feeds: bind(FeedService, UNARY_DEADLINE_MS),
    reactions: bind(ReactionService, UNARY_DEADLINE_MS),
    notifications: bind(NotificationService, UNARY_DEADLINE_MS),
    moderation: bind(ModerationService, UNARY_DEADLINE_MS),
    media: bind(MediaService, UNARY_DEADLINE_MS),
    pages: bind(PageService, UNARY_DEADLINE_MS),
    node: bind(NodeService, UNARY_DEADLINE_MS),
    tags: bind(TagService, UNARY_DEADLINE_MS),
    communities: bind(CommunityService, UNARY_DEADLINE_MS),
    messages: bind(DirectMessageService, UNARY_DEADLINE_MS),
    e2ee: bind(E2eeService, UNARY_DEADLINE_MS),
    filters: bind(FilterService, UNARY_DEADLINE_MS),
    filterLists: bind(FilterListService, UNARY_DEADLINE_MS),
    labels: bind(LabelService, UNARY_DEADLINE_MS),
    appeals: bind(AppealService, UNARY_DEADLINE_MS),
    privacy: bind(PrivacyService, UNARY_DEADLINE_MS),
    // StartDemo seeds a sandbox (posts, follows, likes) in one call: give it the long auth budget.
    onboarding: bind(OnboardingService, AUTH_DEADLINE_MS),
    session: new SessionManager(
      options.credentialStore === void 0 ? { transport: options.transport } : { transport: options.transport, credentialStore: options.credentialStore }
    )
  };
}
function bindService(service, transport, deadlineMs, clientName, clientVersion) {
  const client = createClient2(service, transport);
  const handler = {
    get(target, property, receiver) {
      const value = Reflect.get(target, property, receiver);
      if (typeof value !== "function") return value;
      const method = value;
      return (request, callOptions) => {
        const headers = new Headers(callOptions?.headers);
        if (!headers.has(METADATA_KEYS.requestId))
          headers.set(METADATA_KEYS.requestId, crypto.randomUUID());
        headers.set(METADATA_KEYS.client, clientName);
        headers.set(METADATA_KEYS.clientVersion, clientVersion);
        return method(request, {
          timeoutMs: deadlineMs,
          ...callOptions,
          headers
        });
      };
    }
  };
  return new Proxy(client, handler);
}

// src/pagination.ts
var DEFAULT_LIMIT = 20;
async function* paginate(fetchPage, extractItems, options) {
  let cursor = options?.startCursor ?? "";
  const limit = options?.limit ?? DEFAULT_LIMIT;
  for (; ; ) {
    const response = await fetchPage(cursor, limit);
    for (const item of extractItems(response)) {
      yield item;
    }
    const page = response.page;
    if (page === void 0 || !page.hasMore || page.nextCursor.length === 0) return;
    cursor = page.nextCursor;
  }
}
export {
  InMemoryCredentialStore,
  SESSION_REFRESHED_EVENT,
  SessionManager,
  createPatchesApi,
  decodeJwtExpiry,
  describeError,
  isDefinitiveAuthFailure,
  isPrivacyAckRequired,
  isSignInRequired,
  isTransientError,
  paginate
};
//# sourceMappingURL=index.js.map